/**
 * What may go into the documents bucket, and under what name.
 *
 * These limits used to live inside supabaseStore.js as module-private
 * constants, which meant only the two upload paths in that file could
 * use them. Three others — the onboarding uploads in Onboarding.jsx
 * and Admin.jsx, and the tax statement import in TaxFormsSection.jsx —
 * passed the browser's `file.type` straight to storage with nothing
 * checking it.
 *
 * The allowlist is the point. It has never included text/html or
 * image/svg+xml, and both carry script: Supabase serves an object back
 * with the content type it was stored under, so an .html uploaded as
 * text/html is a live page on the storage origin, reachable by a link
 * that looks like the company's own. That origin is not the app's, so
 * this is not a route to a session — it is a phishing page hosted
 * under a name people have been taught to trust, which is quite enough.
 *
 * Mirrors api/_lib/validate.js on the server side. Deliberately
 * duplicated: the frontend cannot import from api/.
 */

export const MAX_DOC_BYTES = 25 * 1024 * 1024;

export const ALLOWED_DOC_MIME = new Set([
  'application/pdf',
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain', 'text/csv',
]);

export const DOC_MIME_BY_EXT = new Map([
  ['.pdf', 'application/pdf'],
  ['.jpg', 'image/jpeg'],
  ['.jpeg', 'image/jpeg'],
  ['.png', 'image/png'],
  ['.webp', 'image/webp'],
  ['.heic', 'image/heic'],
  ['.heif', 'image/heif'],
  ['.tif', 'image/tiff'],
  ['.tiff', 'image/tiff'],
  ['.doc', 'application/msword'],
  ['.docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  ['.xls', 'application/vnd.ms-excel'],
  ['.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  ['.txt', 'text/plain'],
  ['.csv', 'text/csv'],
]);

/**
 * The type to store an object under.
 *
 * `file.type` comes from the browser, which takes it from the OS by
 * extension — so it is a hint, not a fact, and the allowlist below is
 * what makes it safe to use. Falls back to the extension when the
 * browser offers nothing.
 */
export function mimeForFile(file) {
  const explicit = String(file?.type || '').toLowerCase();
  if (explicit) return explicit;
  const name = String(file?.name || '').toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
  return DOC_MIME_BY_EXT.get(ext) || 'application/octet-stream';
}

/** Throws with a message meant for the person who picked the file. */
export function assertSupportedDocument({ mime, size, name }) {
  if (!ALLOWED_DOC_MIME.has(String(mime || '').toLowerCase())) {
    throw new Error(`Unsupported file type: ${mime || 'unknown'}. Accepted: PDF, JPG/PNG/WebP/HEIC/TIFF, Word, Excel, TXT, CSV.`);
  }
  if (Number(size) > MAX_DOC_BYTES) {
    throw new Error(`File too large (${(Number(size) / 1024 / 1024).toFixed(1)} MB). Max 25 MB per document.`);
  }
  if (!name) {
    throw new Error('Document upload is missing a file name.');
  }
}

/**
 * A file name that cannot climb out of its folder or introduce a path
 * separator. Every caller already did some version of this; this is
 * the one they share.
 */
export function safeDocumentFileName(name, fallback = 'document') {
  const cleaned = String(name || '').replace(/[^a-zA-Z0-9._-]/g, '_').replace(/^\.+/, '');
  return cleaned || fallback;
}
